import type { StaticModelIdType } from "@app/types/assistant/models/models";
import type { ReasoningEffortSupport } from "@app/types/assistant/models/types";

// Supported reasoning efforts per static model, mirroring SUPPORTED_MODEL_CONFIGS.
// Kept in sync via model_tiers.test.ts; literals must stay as const for per-model tier typing.
export const STATIC_MODEL_SUPPORTED_REASONING_EFFORTS = {
  "gpt-3.5-turbo": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "gpt-4-turbo": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "gpt-4o": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "gpt-4.1-2025-04-14": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "gpt-4.1-mini-2025-04-14": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "gpt-4o-2024-08-06": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "gpt-4o-mini": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "gpt-5.1": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "gpt-5.2": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: false,
  },
  "gpt-5.4-mini": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: false,
  },
  "gpt-5.4": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: false,
  },
  "gpt-5.5": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: false,
  },
  "gpt-6-astra": {
    none: false,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: true,
  },
  "gpt-6.1-sol": {
    none: false,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: true,
  },
  "gpt-6-sol": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: true,
  },
  "gpt-5.6-sol": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: true,
  },
  "gpt-5.6-terra": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: true,
  },
  "gpt-5.6-terra-long-context": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: true,
  },
  "gpt-6-luna": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: true,
  },
  "gpt-5.6-luna": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: true,
  },
  "gpt-5.4-nano": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: false,
  },
  "gpt-5": {
    none: false,
    minimal: true,
    low: true,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "gpt-5-mini": {
    none: false,
    minimal: true,
    low: true,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "gpt-5-nano": {
    none: false,
    minimal: true,
    low: true,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  o1: {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "o1-mini": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  o3: {
    none: false,
    minimal: false,
    low: false,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "o3-mini": {
    none: false,
    minimal: false,
    low: false,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "o4-mini": {
    none: false,
    minimal: false,
    low: false,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "claude-4-opus-20250514": {
    none: true,
    minimal: false,
    low: false,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "claude-4-sonnet-20250514": {
    none: true,
    minimal: false,
    low: false,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "claude-sonnet-4-5-20250929": {
    none: true,
    minimal: false,
    low: false,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "claude-opus-4-5-20251101": {
    none: true,
    minimal: false,
    low: false,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "claude-opus-4-6": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: false,
    maximal: true,
  },
  "claude-opus-4-7": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: true,
  },
  "claude-opus-4-8": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: true,
  },
  "claude-opus-5": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: true,
  },
  "claude-opus-5-5": {
    none: false,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: true,
  },
  "claude-fable-5": {
    none: false,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: true,
  },
  "claude-fable-5-1": {
    none: false,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: true,
  },
  "claude-sonnet-5-5": {
    none: false,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: true,
  },
  "claude-sonnet-5": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: true,
  },
  "claude-sonnet-4-6": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: false,
    maximal: true,
  },
  "claude-3-opus-20240229": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "claude-3-5-sonnet-20240620": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "claude-3-5-sonnet-20241022": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "claude-3-7-sonnet-20250219": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "claude-3-haiku-20240307": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "claude-3-5-haiku-20241022": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "claude-haiku-5-5": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: true,
  },
  "claude-haiku-4-5-20251001": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "mistral-large-latest": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "mistral-large-4": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "mistral-medium": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "mistral-medium-3-5": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "mistral-small-latest": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "codestral-latest": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "gemini-2.5-flash": {
    none: true,
    minimal: false,
    low: true,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "gemini-2.5-flash-lite": {
    none: true,
    minimal: false,
    low: true,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "gemini-2.5-pro": {
    none: false,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "gemini-3-pro-preview": {
    none: false,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "gemini-3.1-flash-lite": {
    none: true,
    minimal: true,
    low: true,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "gemini-3.5-flash-lite": {
    none: false,
    minimal: true,
    low: true,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "gemini-3.1-flash-lite-preview": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "gemini-3.1-pro-preview": {
    none: false,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "gemini-3-flash-preview": {
    none: false,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "gemini-3.5-flash": {
    none: true,
    minimal: true,
    low: true,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "gemini-3.6-flash": {
    none: false,
    minimal: true,
    low: true,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "gemini-3.7-flash": {
    none: false,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "gemini-3.8-flash": {
    none: false,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "gemini-4-argon": {
    none: false,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "deepseek-chat": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "accounts/fireworks/models/deepseek-v3p2": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "accounts/fireworks/models/deepseek-v4-pro": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: true,
    xhigh: false,
    maximal: true,
  },
  "accounts/fireworks/models/deepseek-v4-pro-0813": {
    none: false,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "accounts/fireworks/models/deepseek-v4-flash-0731": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "accounts/fireworks/models/deepseek-v4p1-flash": {
    none: true,
    minimal: false,
    low: true,
    medium: false,
    high: true,
    xhigh: false,
    maximal: true,
  },
  "accounts/fireworks/models/kimi-k2-instruct-0905": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "accounts/fireworks/models/kimi-k2p5": {
    none: true,
    minimal: false,
    low: false,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "accounts/fireworks/models/kimi-k2p6": {
    none: true,
    minimal: false,
    low: false,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "accounts/fireworks/models/kimi-k3": {
    none: false,
    minimal: false,
    low: true,
    medium: false,
    high: true,
    xhigh: false,
    maximal: true,
  },
  "accounts/fireworks/models/minimax-m2p5": {
    none: true,
    minimal: false,
    low: false,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "accounts/fireworks/models/glm-5": {
    none: true,
    minimal: false,
    low: false,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "accounts/fireworks/models/glm-5p2": {
    none: false,
    minimal: false,
    low: false,
    medium: false,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "accounts/fireworks/models/glm-5p3": {
    none: false,
    minimal: false,
    low: true,
    medium: false,
    high: true,
    xhigh: false,
    maximal: true,
  },
  "accounts/fireworks/models/glm-5p3-flash": {
    none: false,
    minimal: false,
    low: true,
    medium: false,
    high: true,
    xhigh: false,
    maximal: true,
  },
  "accounts/fireworks/models/inkling": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: true,
  },
  "grok-3-latest": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "grok-3-mini-latest": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "grok-4.5": {
    none: false,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "grok-4.6": {
    none: false,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: false,
  },
  "grok-4.7": {
    none: false,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: false,
  },
  "grok-4-latest": {
    none: true,
    minimal: false,
    low: false,
    medium: true,
    high: true,
    xhigh: false,
    maximal: false,
  },
  "grok-4-fast-reasoning-latest": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "grok-4-fast-non-reasoning-latest": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "grok-4-1-fast-non-reasoning-latest": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "grok-4-1-fast-reasoning-latest": {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  "simulated-failure-model": {
    none: true,
    minimal: false,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: false,
  },
  noop: {
    none: true,
    minimal: true,
    low: true,
    medium: true,
    high: true,
    xhigh: true,
    maximal: true,
  },
  auto: {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  auto_fast: {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
  auto_complex: {
    none: true,
    minimal: false,
    low: false,
    medium: false,
    high: false,
    xhigh: false,
    maximal: false,
  },
} as const satisfies Record<StaticModelIdType, ReasoningEffortSupport>;
